import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Save as SaveIcon, Business as BusinessIcon, ArrowBack as ArrowBackIcon, ContentCopy as ContentCopyIcon} from "@mui/icons-material";
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import tenantService from "../services/tenantService";
import lookupService from "../services/lookupService";
import LogoUpload from "../components/LogoUpload";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import { toast } from 'react-toastify';
import { isTenantAdmin, isSuperAdmin } from "../utils/userHelper";
import ZakazujMeLogo from "../assets/ZakazujMeLogo4.png";

const normalizeSubdomainValue = (value = '') => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

const TenantSettings = ({ isEdit }) => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const params = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientTypes, setClientTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    logoUrl: '',
    clientType: '',
    subdomain: '',
    numberOfEmployees: '',
    address: {
      street: '',
      city: '',
      number: '',
    },
    email: '',
    phone: '',
    website: '',
    description: '',
    isActive: true,
    workingHours: {
      monday: { isOpen: true, open: '09:00', close: '17:00' },
      tuesday: { isOpen: true, open: '09:00', close: '17:00' },
      wednesday: { isOpen: true, open: '09:00', close: '17:00' },
      thursday: { isOpen: true, open: '09:00', close: '17:00' },
      friday: { isOpen: true, open: '09:00', close: '17:00' },
      saturday: { isOpen: false, open: '09:00', close: '17:00' },
      sunday: { isOpen: false, open: '09:00', close: '17:00' },
    },
    settings: {
      allowOnlineBooking: false,
      requireConfirmation: false,
      enableQuickAppointment: false,
      defaultAppointmentMode: 'quick',
      hasShifts: false,
      autoAssignEmployee: false,
      externalUserRegistration: false,
      approvalOfExternalUserRegistration: false,
      partiallyRequireConfirmation: false,
      slotDuration: null,
    },
    shifts: [],
  });

  const isTenantAdminCheck = isTenantAdmin(currentUser, "");
  const isSuperAdminUser = isSuperAdmin(currentUser);

  const defaultWorkingHours = useMemo(() => ({
    monday: { isOpen: true, open: '09:00', close: '17:00' },
    tuesday: { isOpen: true, open: '09:00', close: '17:00' },
    wednesday: { isOpen: true, open: '09:00', close: '17:00' },
    thursday: { isOpen: true, open: '09:00', close: '17:00' },
    friday: { isOpen: true, open: '09:00', close: '17:00' },
    saturday: { isOpen: false, open: '09:00', close: '17:00' },
    sunday: { isOpen: false, open: '09:00', close: '17:00' },
  }), []);

  // QR / user registration link state
  const [userRegQrDataUrl, setUserRegQrDataUrl] = useState('');
  const [generatingUserRegQr, setGeneratingUserRegQr] = useState(false);

  // Generate QR when the tenant's userRegistrationLink changes
  useEffect(() => {
    const link = formData.userRegistrationLink;
    if (!link) {
      setUserRegQrDataUrl('');
      return;
    }

    const fullUrl = (typeof window !== 'undefined')
      ? (link.startsWith('http') ? link : `${window.location.origin}${link}`)
      : link;

    let cancelled = false;
    const gen = async () => {
      try {
        setGeneratingUserRegQr(true);
        const dataUrl = await QRCode.toDataURL(fullUrl, { margin: 1, width: 240 });
        if (!cancelled) setUserRegQrDataUrl(dataUrl);
      } catch (err) {
        console.error('QR generation failed', err);
        if (!cancelled) setUserRegQrDataUrl('');
      } finally {
        if (!cancelled) setGeneratingUserRegQr(false);
      }
    };

    gen();

    return () => { cancelled = true; };
  }, [formData.userRegistrationLink]);

  const handleCopyUserRegLink = async () => {
    const link = formData.userRegistrationLink;
    const fullUrl = link ? (link.startsWith('http') ? link : `${window.location.origin}${link}`) : '';
    if (!fullUrl) {
      toast.info('Nema linka za kopiranje');
      return;
    }

    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Link kopiran u clipboard');
    } catch (err) {
      console.error('Copy failed', err);
      toast.error('Kopiranje neuspešno');
    }
  };

  const handleDownloadQr = async () => {
    if (!userRegQrDataUrl) return;

    try {
      // Učitaj logo kao data URL
      const logoResponse = await fetch(ZakazujMeLogo);
      const logoBlob = await logoResponse.blob();
      const logoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(logoBlob);
      });

      // Kreiraj privremeni DOM element sa istim stilovima
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = `
        font-family: Arial, sans-serif;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 600px;
        width: 500px;
        margin: 0;
        padding: 20px;
        background-color: #ffffff;
        position: absolute;
        left: -9999px;
        top: -9999px;
      `;

      const container = document.createElement('div');
      container.style.cssText = `
        background: linear-gradient(180deg, #0F1431 0%, #171B46 100%);
        padding: 40px;
        border-radius: 10px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        text-align: center;
        width: 100%;
        color: white;
      `;

      const logoImg = document.createElement('img');
      logoImg.src = logoDataUrl;
      logoImg.alt = 'ZakazujMe Logo';
      logoImg.style.cssText = `
        max-width: 200px;
        max-height: 120px;
        margin-bottom: 20px;
      `;

      const tenantName = document.createElement('h2');
      tenantName.textContent = formData.name || 'Naziv radnje';
      tenantName.style.cssText = `
        color: #ffffff;
        margin-bottom: 20px;
        font-size: 20px;
        font-weight: bold;
        text-align: center;
      `;

      const title = document.createElement('h1');
      title.textContent = 'Registracija korisnika';
      title.style.cssText = `
        color: #ffffff;
        margin-bottom: 20px;
        font-size: 24px;
        font-weight: bold;
      `;

      const description = document.createElement('p');
      description.textContent = 'Skenirajte QR kod za registraciju';
      description.style.cssText = `
        color: rgba(255, 255, 255, 0.9);
        margin-bottom: 20px;
        font-size: 16px;
      `;

      const qrImg = document.createElement('img');
      qrImg.src = userRegQrDataUrl;
      qrImg.alt = 'QR kod za registraciju';
      qrImg.style.cssText = `
        width: 240px;
        height: 240px;
        margin: 20px 0;
      `;

      // Sastavi elemente
      container.appendChild(logoImg);
      container.appendChild(tenantName);
      container.appendChild(title);
      container.appendChild(description);
      container.appendChild(qrImg);
      tempDiv.appendChild(container);
      document.body.appendChild(tempDiv);

      // Koristi html2canvas da pretvori u PNG
      const canvas = await html2canvas(tempDiv, {
        scale: 2, // Veća rezolucija
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
      });

      // Ukloni privremeni element
      document.body.removeChild(tempDiv);

      // Konvertuj canvas u blob i download-uj
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Greška pri generisanju PNG slike');
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'qr-kod-registracija-korisnika.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');

    } catch (error) {
      console.error('Error generating PNG:', error);
      toast.error('Greška pri generisanju PNG slike');
    }
  };

  

  const fetchClientTypes = async () => {
    try {
      const response = await lookupService.getAllLookupValues('_tip_klijenta');
      setClientTypes(response || []);
    } catch (error) {
      console.error('Error fetching client types:', error);
      setClientTypes([]);
    }
  };

  const fetchTenantDataForEdit = React.useCallback(async (tenantId) => {
    try {
      setLoading(true);
      const response = await tenantService.getTenantById(tenantId);
      
      const tenant = response.data || response;
      
      // Populate form data
      setFormData({
        name: tenant.name || '',
        logoUrl: tenant.logoUrl || '',
        clientType: tenant.clientType || tenant.businessType || '',
        subdomain: tenant.subdomain || '',
        numberOfEmployees: tenant.numberOfEmployees || '',
        address: {
          street: tenant.address?.street || '',
          city: tenant.address?.city || '',
          number: tenant.address?.number || '',
        },
        email: tenant.email || '',
        phone: tenant.phone || '',
        website: tenant.website || '',
        description: tenant.description || '',
        isActive: tenant.isActive !== undefined ? tenant.isActive : true,
        workingHours: tenant.workingHours || defaultWorkingHours,
        userRegistrationLink: tenant.userRegistrationLink || '',
        settings: {
          allowOnlineBooking: tenant.settings?.allowOnlineBooking || false,
          requireConfirmation: tenant.settings?.requireConfirmation || false,
          enableQuickAppointment: tenant.settings?.enableQuickAppointment || false,
          defaultAppointmentMode: tenant.settings?.defaultAppointmentMode || 'quick',
          hasShifts: tenant.settings?.hasShifts || false,
          autoAssignEmployee: tenant.settings?.autoAssignEmployee || false,
          externalUserRegistration: tenant.settings?.externalUserRegistration || false,
          approvalOfExternalUserRegistration: tenant.settings?.approvalOfExternalUserRegistration || false,
          partiallyRequireConfirmation: tenant.settings?.partiallyRequireConfirmation || false,
          slotDuration: tenant.settings?.slotDuration || null,
        },
        shifts: tenant.shifts || [],
      });
    } catch (err) {
      console.error("Error fetching tenant data:", err);
      toast.error("Greška pri učitavanju podataka o klijentu");
    } finally {
      setLoading(false);
    }
  }, [defaultWorkingHours]);

  const fetchTenantData = React.useCallback(async () => {
    try {
      setLoading(true);
      const tenantId = currentUser?.tenantId?._id || currentUser?.tenantId;
      const response = await tenantService.getTenantById(tenantId);
      
      const tenant = response.data || response;
      
      // Populate form data
      setFormData({
        name: tenant.name || '',
        logoUrl: tenant.logoUrl || '',
        clientType: tenant.clientType || tenant.businessType || '',
        subdomain: tenant.subdomain || '',
        numberOfEmployees: tenant.numberOfEmployees || '',
        address: {
          street: tenant.address?.street || '',
          city: tenant.address?.city || '',
          number: tenant.address?.number || '',
        },
        email: tenant.email || '',
        phone: tenant.phone || '',
        website: tenant.website || '',
        description: tenant.description || '',
        isActive: tenant.isActive !== undefined ? tenant.isActive : true,
        workingHours: tenant.workingHours || defaultWorkingHours,
        userRegistrationLink: tenant.userRegistrationLink || '',
        settings: {
          allowOnlineBooking: tenant.settings?.allowOnlineBooking || false,
          requireConfirmation: tenant.settings?.requireConfirmation || false,
          enableQuickAppointment: tenant.settings?.enableQuickAppointment || false,
          defaultAppointmentMode: tenant.settings?.defaultAppointmentMode || 'quick',
          hasShifts: tenant.settings?.hasShifts || false,
          autoAssignEmployee: tenant.settings?.autoAssignEmployee || false,
          externalUserRegistration: tenant.settings?.externalUserRegistration || false,
          approvalOfExternalUserRegistration: tenant.settings?.approvalOfExternalUserRegistration || false,
          partiallyRequireConfirmation: tenant.settings?.partiallyRequireConfirmation || false,
          slotDuration: tenant.settings?.slotDuration || null,
        },
        shifts: tenant.shifts || [],
      });
    } catch (err) {
      console.error("Error fetching tenant data:", err);
      toast.error("Greška pri učitavanju podataka o klijentu");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.tenantId, defaultWorkingHours]);

  // Init effect - call after callbacks are defined
  useEffect(() => {
    if (isEdit && params.id) {
      if (params.id === 'new') {
        // New tenant creation
        setFormData({
          name: '',
          logoUrl: '',
          clientType: '',
          numberOfEmployees: '',
          address: {
            street: '',
            city: '',
            number: '',
          },
          email: '',
          phone: '',
          website: '',
          description: '',
          isActive: true,
          workingHours: defaultWorkingHours,
          settings: {
            allowOnlineBooking: false,
            requireConfirmation: false,
            enableQuickAppointment: false,
            defaultAppointmentMode: 'quick',
            hasShifts: false,
            autoAssignEmployee: false,
            externalUserRegistration: false,
            approvalOfExternalUserRegistration: false,
          },
          shifts: [],
        });
        fetchClientTypes();
      } else {
        fetchClientTypes();
        fetchTenantDataForEdit(params.id);
      }
    } else if (isTenantAdminCheck) {
      fetchTenantData();
    }
  }, [isEdit, params.id, isTenantAdminCheck, fetchTenantData, fetchTenantDataForEdit, defaultWorkingHours]);

  const handleSubmit = async () => {
    if (saving) return; // Prevent double submission
    setSaving(true);
    try {
      const tenantId = isEdit ? params.id : (currentUser?.tenantId?._id || currentUser?.tenantId);
      
      // Validacija
      if (!formData.name.trim()) {
        toast.error("Naziv klijenta je obavezan");
        setSaving(false);
        return;
      }

      if (isSuperAdminUser && (formData.numberOfEmployees === '' || formData.numberOfEmployees === null || formData.numberOfEmployees === undefined)) {
        toast.error("Broj zaposlenih je obavezan");
        setSaving(false);
        return;
      }

      const dataToSend = {
        ...formData,
      };

      if (isSuperAdminUser) {
        dataToSend.subdomain = normalizeSubdomainValue(formData.subdomain);
        // Konvertuj numberOfEmployees u broj
        dataToSend.numberOfEmployees = formData.numberOfEmployees !== '' ? parseInt(formData.numberOfEmployees, 10) : null;
      } else {
        delete dataToSend.subdomain;
        delete dataToSend.numberOfEmployees;
        delete dataToSend.clientType;

      }

      if (isEdit) {
        if (params.id === 'new') {
          await tenantService.createTenant(dataToSend);
          toast.success("Klijent je uspešno kreiran");
        } else {
          await tenantService.updateTenant(tenantId, dataToSend);
          toast.success("Klijent je uspešno ažuriran");
        }
        navigate("/tenants");
      } else {
        await tenantService.updateTenant(tenantId, dataToSend);
        toast.success("Podešavanja klijenta su uspešno sačuvana");
        // Refresh data
        fetchTenantData();
      }
    } catch (err) {
      console.error("Error updating tenant:", err);
      toast.error(err.response?.data?.message || err.message || "Greška pri ažuriranju podešavanja klijenta");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUploadSuccess = (logoUrl) => {
    setFormData(prev => ({
      ...prev,
      logoUrl: logoUrl
    }));
  };

  const handleLogoRemove = () => {
    setFormData(prev => ({
      ...prev,
      logoUrl: ''
    }));
  };

  const handleLogoError = (errorMessage) => {
    toast.error(errorMessage);
  };

  if (!isTenantAdminCheck && !isSuperAdminUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nemate pristup ovoj stranici.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        {isEdit && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tenants')}
            sx={{
              mb: 2,
              ...commonStyles.textPrimary,
            }}
          >
            Nazad
          </Button>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 16px rgba(59, 130, 246, 0.3)',
            }}
          >
            <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ ...commonStyles.textPrimary, fontWeight: 700 }}>
              {isEdit ? 'Uredi radnju' : 'Podešavanja radnje'}
            </Typography>
            <Typography variant="body2" sx={{ ...commonStyles.textSecondary, mt: 0.5 }}>
              {isEdit ? 'Ažurirajte podatke o radnji' : 'Upravljajte informacijama vaše radnje'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Basic Info & Address */}
        <Grid item xs={12} lg={8}>
          {/* Logo i Naziv */}
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Logo i naziv
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box>
                  <LogoUpload
                    logoUrl={formData.logoUrl}
                    onUploadSuccess={handleLogoUploadSuccess}
                    onRemove={handleLogoRemove}
                    onError={handleLogoError}
                    maxSizeMB={5}
                    width={200}
                    height={120}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Naziv klijenta"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    sx={{ ...commonStyles.formField, mb: 2 }}
                    required
                  />
                  {isSuperAdminUser && (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel sx={commonStyles.textSecondary}>Tip klijenta</InputLabel>
                        <Select
                          value={formData.clientType}
                          label="Tip klijenta"
                          onChange={(e) => setFormData({ ...formData, clientType: e.target.value })}
                          sx={commonStyles.selectField}
                        >
                          {clientTypes.map((type) => (
                            <MenuItem key={type._id} value={type._id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Subdomen"
                        placeholder="npr. frizersalon"
                        helperText="Biće korišćen kao frizersalon.zakazuj.com"
                        value={formData.subdomain}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subdomain: normalizeSubdomainValue(e.target.value),
                          })
                        }
                        sx={{ ...commonStyles.formField }}
                      />
                      <TextField
                        fullWidth
                        label="Broj zaposlenih"
                        type="number"
                        inputProps={{ min: 0, step: 1 }}
                        value={formData.numberOfEmployees}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                          setFormData({ ...formData, numberOfEmployees: value >= 0 ? value : '' });
                        }}
                        sx={{ ...commonStyles.formField }}
                        required
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Kontakt informacije */}
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Kontakt informacije
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    sx={commonStyles.formField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    sx={commonStyles.formField}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    sx={commonStyles.formField}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Opis"
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    sx={commonStyles.formField}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Adresa */}
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Adresa
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Grad"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                    sx={commonStyles.formField}
                  />
                </Grid>

                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Ulica"
                    value={formData.address.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value }
                    })}
                    sx={commonStyles.formField}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Broj"
                    value={formData.address.number}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, number: e.target.value }
                    })}
                    sx={commonStyles.formField}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Radno vreme */}
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Radno vreme
              </Typography>

              <Grid container spacing={2}>
                {[
                  { key: 'monday', label: 'Ponedeljak', short: 'Pon' },
                  { key: 'tuesday', label: 'Utorak', short: 'Uto' },
                  { key: 'wednesday', label: 'Sreda', short: 'Sre' },
                  { key: 'thursday', label: 'Četvrtak', short: 'Čet' },
                  { key: 'friday', label: 'Petak', short: 'Pet' },
                  { key: 'saturday', label: 'Subota', short: 'Sub' },
                  { key: 'sunday', label: 'Nedelja', short: 'Ned' }
                ].map((day) => (
                  <Grid item xs={12} sm={6} md={4} key={day.key}>
                    <Box
                      sx={{
                        p: 2,
                        border: `1px solid ${customColors.border.light}`,
                        borderRadius: 2,
                        background: formData.workingHours[day.key].isOpen 
                          ? (isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)')
                          : (isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              background: formData.workingHours[day.key].isOpen 
                                ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                                : (isDarkMode ? 'rgba(100, 116, 139, 0.3)' : 'rgba(148, 163, 184, 0.3)'),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 1.5,
                              boxShadow: formData.workingHours[day.key].isOpen ? '0 4px 8px rgba(59, 130, 246, 0.3)' : 'none',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 600 }}>
                              {day.short}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                            {day.label}
                          </Typography>
                        </Box>
                        <Switch
                          checked={formData.workingHours[day.key].isOpen}
                          onChange={(e) => setFormData({
                            ...formData,
                            workingHours: {
                              ...formData.workingHours,
                              [day.key]: { 
                                ...formData.workingHours[day.key],
                                isOpen: e.target.checked,
                              }
                            }
                          })}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: customColors.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: customColors.primary.main,
                            },
                          }}
                        />
                      </Box>

                      {formData.workingHours[day.key].isOpen && (
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                          <Box>
                            <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                              Otvoreno
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={formData.workingHours[day.key].open || '09:00'}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  workingHours: {
                                    ...formData.workingHours,
                                    [day.key]: { 
                                      ...formData.workingHours[day.key],
                                      open: e.target.value 
                                    }
                                  }
                                })}
                                sx={{
                                  ...commonStyles.selectField,
                                  fontSize: '0.75rem',
                                  height: 32,
                                }}
                              >
                                {[
                                  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30',
                                  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
                                  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
                                  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
                                  '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
                                  '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
                                ].map((time) => (
                                  <MenuItem key={time} value={time} sx={{ fontSize: '0.75rem' }}>
                                    {time}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                              Zatvoreno
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={formData.workingHours[day.key].close || '17:00'}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  workingHours: {
                                    ...formData.workingHours,
                                    [day.key]: { 
                                      ...formData.workingHours[day.key],
                                      close: e.target.value 
                                    }
                                  }
                                })}
                                sx={{
                                  ...commonStyles.selectField,
                                  fontSize: '0.75rem',
                                  height: 32,
                                }}
                              >
                                {[
                                  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30',
                                  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
                                  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
                                  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
                                  '18:00', '18:30', '19:00', '19:30', '20:00', '20:30',
                                  '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
                                ].map((time) => (
                                  <MenuItem key={time} value={time} sx={{ fontSize: '0.75rem' }}>
                                    {time}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Settings & Status */}
        <Grid item xs={12} lg={4}>
          {/* Status - vidljivo samo super adminu */}
          {isSuperAdmin(currentUser) && (
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Status
              </Typography>

              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                border: `1px solid ${customColors.primary.main}40`,
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: customColors.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: customColors.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                      {formData.isActive ? 'Klijent je aktivan' : 'Klijent je neaktivan'}
                    </Typography>
                  }
                />
              </Box>
            </CardContent>
          </Card>
          )}

          {/* Podešavanja */}
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Podešavanja
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${customColors.border.light}`,
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.settings.allowOnlineBooking}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            allowOnlineBooking: e.target.checked
                          }
                        })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors.primary.main,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                          Online rezervacije
                        </Typography>
                        <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                          Omogući klijentima online rezervisanje
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${customColors.border.light}`,
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.settings.requireConfirmation}
                        onChange={(e) => {
                          const newRequireConfirmation = e.target.checked;
                          console.log('requireConfirmation changed to:', newRequireConfirmation);
                          console.log('current partiallyRequireConfirmation:', formData.settings.partiallyRequireConfirmation);
                          
                          setFormData(prevData => ({
                            ...prevData,
                            settings: {
                              ...prevData.settings,
                              requireConfirmation: newRequireConfirmation,
                              // Ako se isključuje requireConfirmation, isključi i partiallyRequireConfirmation
                              partiallyRequireConfirmation: newRequireConfirmation ? prevData.settings.partiallyRequireConfirmation : false
                            }
                          }));
                        }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors.primary.main,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                          Zahtevaj potvrdu
                        </Typography>
                        <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                          Zahtevaj potvrdu pre nego što se termin zaključi
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${customColors.border.light}`,
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.settings.partiallyRequireConfirmation}
                        onChange={(e) => {
                          const newPartiallyRequireConfirmation = e.target.checked;
                          console.log('partiallyRequireConfirmation changed to:', newPartiallyRequireConfirmation);
                          console.log('current requireConfirmation:', formData.settings.requireConfirmation);
                          
                          setFormData(prevData => ({
                            ...prevData,
                            settings: {
                              ...prevData.settings,
                              partiallyRequireConfirmation: newPartiallyRequireConfirmation,
                              // Ako se uključuje partiallyRequireConfirmation, uključi i requireConfirmation
                              requireConfirmation: newPartiallyRequireConfirmation ? true : prevData.settings.requireConfirmation
                            }
                          }));
                        }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors.primary.main,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                          Parcijalno zahtevanje potvrde
                        </Typography>
                        <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                          Zahtevaj potvrdu samo za određene korisnike
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${customColors.border.light}`,
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.settings.enableQuickAppointment}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            enableQuickAppointment: e.target.checked
                          }
                        })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors.primary.main,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                          Kreiraj termine za neregistrovane korisnike
                        </Typography>
                        <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                          Omogući kreiranje termina bez registracije korisnika
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                  border: `1px solid ${customColors.border.light}`,
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.settings.autoAssignEmployee}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            autoAssignEmployee: e.target.checked
                          }
                        })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: customColors.primary.main,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: customColors.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                          Auto dodela
                        </Typography>
                        <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                          Automatski dodeli zaposlenog terminu
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${customColors.border.light}`,
                  }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.settings.externalUserRegistration}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: {
                              ...formData.settings,
                              externalUserRegistration: e.target.checked,
                              // Automatski isključi approvalOfExternalUserRegistration kada se isključi external registration
                              approvalOfExternalUserRegistration: e.target.checked ? formData.settings.approvalOfExternalUserRegistration : false
                            }
                          })}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: customColors.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: customColors.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                            Dozvoli eksternu registraciju
                          </Typography>
                          <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                            Omogući novim korisnicima da se samostalno registruju
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${customColors.border.light}`,
                  }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.settings.approvalOfExternalUserRegistration}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: {
                              ...formData.settings,
                              approvalOfExternalUserRegistration: e.target.checked,
                              // Automatski uključi externalUserRegistration kada se uključi approval
                              externalUserRegistration: e.target.checked ? true : formData.settings.externalUserRegistration
                            }
                          })}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: customColors.primary.main,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: customColors.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ ...commonStyles.textPrimary, fontWeight: 500 }}>
                            Zahtevaj potvrdu registracije
                          </Typography>
                          <Typography variant="caption" sx={{ ...commonStyles.textSecondary, fontSize: '0.75rem' }}>
                            Zahtevaj odobrenje registracije novih korisnika
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                {formData.settings.enableQuickAppointment && (
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                    border: `1px solid ${customColors.primary.main}40`,
                  }}>
                    <FormControl fullWidth>
                      <InputLabel sx={commonStyles.textSecondary}>Defaultni mod termina</InputLabel>
                      <Select
                        value={formData.settings.defaultAppointmentMode}
                        label="Defaultni mod termina"
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            defaultAppointmentMode: e.target.value
                          }
                        })}
                        sx={commonStyles.selectField}
                      >
                        <MenuItem value="quick">Neregistrovan korisnik (samo ime)</MenuItem>
                        <MenuItem value="select_customer">Izaberi klijenta</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Trajanje slotova - samo za super admin i tenant admin */}
                {(isSuperAdminUser || isTenantAdminCheck) && (
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    background: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${customColors.border.light}`,
                  }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Trajanje slotova (minuti)"
                      value={formData.settings.slotDuration || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {
                          ...formData.settings,
                          slotDuration: e.target.value === '' ? null : parseInt(e.target.value, 10)
                        }
                      })}
                      helperText="Definiše trajanje vremenskih slotova u minutima (npr. 15, 30, 60)"
                      sx={commonStyles.formField}
                      inputProps={{
                        min: 1,
                        step: 1
                      }}
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
          {/* Prijava korisnika putem linka i QR koda - sakrij kada kreiramo novog klijenta (params.id === 'new') */}
          {!(params?.id === 'new') && (
          <Card sx={{ 
            background: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 1)',
            border: isDarkMode ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            mb: 3,
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, ...commonStyles.textPrimary, fontWeight: 600 }}>
                Prijava korisnika putem linka i QR koda
              </Typography>

              <Box>
                <TextField
                  fullWidth
                  label="Link za registraciju korisnika"
                  value={formData.userRegistrationLink || ''}
                  placeholder="Link će biti automatski generisan"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Kopiraj link">
                          <IconButton onClick={handleCopyUserRegLink} edge="end">
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={commonStyles.formField}
                  disabled
                />

                {/* QR kod prikazan ispod linka samo ako postoji generisani QR */}
                {generatingUserRegQr ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : userRegQrDataUrl ? (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={handleDownloadQr}
                        sx={{
                          ...commonStyles.textPrimary,
                          borderColor: customColors.primary.main,
                          color: customColors.primary.main,
                          '&:hover': {
                            borderColor: customColors.primary.main,
                            backgroundColor: `${customColors.primary.main}10`,
                          },
                        }}
                      >
                        Download QR kod
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box sx={{ width: 160, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px dashed ${customColors.border.light}`, borderRadius: 2, p: 1 }}>
                        <img src={userRegQrDataUrl} alt="QR code" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </Box>
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </CardContent>
          </Card>
          )}
        </Grid>
      </Grid>

      {/* Save button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mt: 4,
        position: 'sticky',
        bottom: 24,
      }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
          size="large"
          sx={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
            color: 'white',
            fontWeight: 600,
            px: 6,
            py: 1.5,
            fontSize: '1rem',
            borderRadius: 2,
            boxShadow: '0 8px 16px rgba(59, 130, 246, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)',
              boxShadow: '0 12px 24px rgba(59, 130, 246, 0.4)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: 'rgba(156, 163, 175, 0.5)',
              color: 'rgba(107, 114, 128, 0.7)',
              boxShadow: 'none',
            },
            transition: 'all 0.2s ease',
          }}
        >
          {saving ? 'Čuvanje...' : 'Sačuvaj podešavanja'}
        </Button>
      </Box>
    </Box>
  );
};

export default TenantSettings;
